import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  LoginRequest,
  LoginResponse,
  User,
  Gusto,
  PuntoVendita,
  Produzione,
  CreaProduzionRequest,
  DashboardStats,
  TrendMensile,
  FiltriAnalytics,
  AnalyticsGusto,
  AnalyticsIngrediente,
  AnalyticsPeriodo,
  AnalyticsConfronto,
  AnalyticsFoodCostGusti,
  Configurazione,
  LogAttivita,
  Ingrediente,
  StatisticheIngredientiResponse,
  CategoriaGusto,
  Allergene,
  Giacenza,
  StockCumulativo,
  CreaGiacenzaRequest,
  Smaltimento,
  StockDisponibileItem,
  CreaSmaltimentoRequest
} from '../types';

// ============================================================
// Controllo di gestione economico
// ============================================================
export type SezioneCe = 'ricavo' | 'costo_variabile' | 'costo_fisso' | 'sotto_linea' | 'finanziario';

export interface CeCategoria {
  id: number;
  codice: string;
  nome: string;
  sezione: SezioneCe;
  ordine: number;
}

export interface CeFornitore {
  id: number;
  nome: string;
  categoria_id: number | null;
  categoria_nome?: string | null;
  categoria_codice?: string | null;
  partita_iva?: string | null;
  codice_fiscale?: string | null;
  referente?: string | null;
  telefono?: string | null;
  email?: string | null;
  pec?: string | null;
  sito?: string | null;
  indirizzo?: string | null;
  cap?: string | null;
  citta?: string | null;
  provincia?: string | null;
  condizioni_pagamento?: string | null;
  giorni_consegna?: string | null;
  fornisce_ingredienti?: boolean;
  attivo?: boolean;
  note?: string | null;
  /** contatori che arrivano con l'elenco */
  ingredienti_forniti?: number;
  alias_registrati?: number;
  speso_ultimi_12_mesi?: number;
}

/** Un ingrediente visto dalla scheda del fornitore. */
export interface IngredienteDelFornitore {
  id: number;
  nome: string;
  unita_misura: string;
  costo_per_unita: number;
  attivo: boolean;
  usato_in_gusti: number;
  prezzo_fornitore: number | null;
  codice_articolo: string | null;
  formato_acquisto: string | null;
}

export interface AliasFornitore {
  id: number;
  alias: string;
  origine: string;
  created_at: string;
}

export interface ConsumoTeoricoRiga {
  ingrediente_id: number;
  ingrediente: string;
  unita_misura: string;
  quantita: number;
  costo: number;
  fornitore_id: number | null;
  fornitore_nome: string | null;
}

export interface SchedaFornitore {
  fornitore: CeFornitore;
  periodo: { da: string; a: string };
  ingredienti: IngredienteDelFornitore[];
  alias: AliasFornitore[];
  spesaMensile?: { mese: string; speso: number; movimenti: number }[];
  consumoTeorico?: ConsumoTeoricoRiga[];
  confronto?: { teorico: number; reale: number; scostamento: number };
}

/** Una riga del confronto teorico/reale, un fornitore per riga. */
export interface ConfrontoFornitore {
  fornitore_id: number | null;
  fornitore_nome: string;
  teorico: number;
  reale: number;
  scostamento: number;
  movimenti: number;
}

/** Un nome scritto a mano negli ingredienti, ancora da abbinare. */
export interface NomeDaAbbinare {
  testo: string;
  ingredienti: number;
  nomi_ingredienti: string[];
  proposte: { id: number; nome: string }[];
}

/** Una fattura elettronica importata. */
export interface CeFattura {
  id: number;
  fornitore_id: number | null;
  fornitore_nome?: string | null;
  fornitore_denominazione: string | null;
  fornitore_partita_iva: string | null;
  numero: string | null;
  data: string | null;
  imponibile: number | null;
  imposta: number | null;
  totale: number | null;
  nome_file: string | null;
  stato: 'importata' | 'da_attribuire' | 'esclusa';
  righe?: number;
  righe_da_abbinare?: number;
}

export interface CeEsitoImportFatture {
  file: number;
  totali: {
    fattureImportate: number;
    fileDuplicati: number;
    fileNonLetti: number;
    righe: number;
    righeAbbinate: number;
    daAttribuire: number;
  };
  senzaFornitore: {
    denominazione: string | null;
    partitaIva: string | null;
    numero: string | null;
    data: string | null;
  }[];
  nonLetti: { file: string; errore: string }[];
}

/** Una voce di fattura che aspetta di sapere a quale ingrediente corrisponde. */
export interface RigaFatturaDaAbbinare {
  fornitore_id: number | null;
  fornitore_nome: string | null;
  codice_articolo: string | null;
  descrizione: string;
  volte: number;
  importo_totale: number;
  ultima_fattura: string | null;
  prezzo_medio: number | null;
  unita_misura: string | null;
}

/** Il prezzo davvero pagato per un ingrediente, contro quello in anagrafica. */
export interface PrezzoDalleFatture {
  ingrediente_id: number;
  ingrediente: string;
  unita_misura: string;
  costo_in_anagrafica: number;
  fornitore_id: number | null;
  fornitore_nome: string | null;
  speso: number;
  quantita: number;
  prezzo_medio: number | null;
  ultima_fattura: string | null;
  righe: number;
}

/** Una regola di classificazione dei movimenti. */
export interface CeRegola {
  id: number;
  nome: string | null;
  pattern: string;
  tipo_match: 'contiene' | 'inizia_con' | 'regex';
  segno: 'entrata' | 'uscita' | null;
  importo_min: number | null;
  importo_max: number | null;
  categoria_id: number | null;
  categoria_nome?: string | null;
  fornitore_id: number | null;
  fornitore_nome?: string | null;
  imposta_stato: 'classificato' | 'escluso';
  priorita: number;
  attiva: boolean;
  volte_applicata: number;
  ultima_applicazione: string | null;
}

/** Un codice articolo di fattura abbinato a un ingrediente. */
export interface RegolaArticolo {
  id: number;
  codice_articolo: string;
  descrizione_fornitore: string | null;
  prezzo_per_unita: number | null;
  fornitore_id: number;
  fornitore_nome: string;
  ingrediente_id: number;
  ingrediente_nome: string;
  unita_misura: string;
}

/** Una voce dichiarata "non e' un ingrediente". */
export interface RegolaArticoloIgnorato {
  id: number;
  codice_articolo: string | null;
  descrizione: string | null;
  motivo: string | null;
  fornitore_id: number | null;
  fornitore_nome: string | null;
}

export interface CeConto {
  id: number;
  nome: string;
  tipo: 'conto_corrente' | 'carta_credito' | 'contanti';
  formato_import: string;
  attivo: boolean;
  movimenti: string | number;
  ultimo_movimento: string | null;
}

export interface CeMovimento {
  id: number;
  data_operazione: string;
  descrizione: string;
  importo: string | number;
  stato: 'da_classificare' | 'classificato' | 'escluso';
  categoria_id: number | null;
  categoria_nome: string | null;
  sezione: SezioneCe | null;
  fornitore_nome: string | null;
  conto_nome: string;
  note: string | null;
}

export interface CeSuggerimento {
  categoriaId: number;
  categoriaNome: string;
  fornitoreId: number | null;
  fornitoreNome: string | null;
  punteggio: number;
  occorrenze: number;
}

export interface CeEsitoImportFile {
  righeLette: number;
  importate: number;
  duplicate: number;
  /** Righe che descrivono un pagamento gia' presente su un altro conto. */
  giaSuAltroConto: number;
  /** Righe di un altro conto escluse perche' e' arrivata la stessa dal conto corrente. */
  sostituiti: number;
  scartate: number;
  classificate: number;
  daClassificare: number;
  periodo: { da: string | null; a: string | null };
  avvisi: string[];
}

export interface CeEsitoImport {
  file: number;
  totali: Omit<CeEsitoImportFile, 'periodo' | 'avvisi'>;
  /** File portati sul conto del loro formato invece che su quello scelto */
  spostati?: { file: string; da: string; a: string; importate: number }[];
  perFile: { file: string; esito: CeEsitoImportFile }[];
  falliti: { file: string; errore: string }[];
}

export interface CeEsitoDuplicati {
  eventiDoppi: number;
  esclusi: number;
  valoreEscluso: number;
  soloAnteprima: boolean;
  esempi: { data: string; descrizione: string; importo: number; conto: string }[];
}

export interface CeEsitoDeleghe {
  file: number;
  deleghe: number;
  abbinati: number;
  riclassificati: number;
  movimentiSenzaDelega: number;
  delegheSenzaMovimento: number;
  falliti: { file: string; errore: string }[];
}

export interface CeEsitoQuietanze {
  file: number;
  lette: number;
  quadrano: number;
  daVerificare: { file: string; saldoDelega: number | null; sommaTributi: number | null }[];
  perNatura: { natura: string; totale: number; codici: string[] }[];
  perIntestatario: {
    codiceFiscale: string; denominazione: string | null;
    totale: number; quietanze: number; personaFisica: boolean | null;
  }[];
  falliti: { file: string; errore: string }[];
}

export interface CeEsitoAmazon {
  file: number;
  ordini: number;
  totale: number;
  conCategoriaRiconosciuta: number;
  ripartizioneStimata: number;
  perCategoria: { codiceCategoria: string; totale: number; ordini: number; stimati: number }[];
  categorieNonMappate: { categoria: string; righe: number }[];
  falliti: { file: string; errore: string }[];
}

export interface EsitoTimbrature {
  file: number;
  totali: { lette: number; importate: number; duplicate: number; scartate: number };
  senzaDipendente: { codiceFiscale: string; dipendente: string; timbrature: number }[];
  falliti: { file: string; errore: string }[];
}

export interface EsitoImportMassivo {
  file: number;
  caricati: number;
  giaPresenti: number;
  perDipendente: { utenteId: number; nome: string; caricati: number; giaPresenti: number }[];
  nonAbbinati: {
    file: string; nomeLetto: string | null; codiceFiscaleLetto?: string | null; motivo: string;
  }[];
  falliti: { file: string; errore: string }[];
  /** Cedolini di cui sono stati letti anche gli importi, non solo archiviato il PDF. */
  cedoliniLetti: number;
  cedoliniNonLetti: { file: string; motivo: string }[];
}

export interface RiepilogoDocumenti {
  id: number;
  nome: string;
  cognome: string | null;
  attivo: boolean;
  documenti: string;
  buste_paga: string;
  anno_piu_recente: number | null;
  non_letti: string;
}

export interface AnagraficaDipendente {
  id: number;
  nome: string;
  cognome: string | null;
  username: string;
  email: string | null;
  telefono: string | null;
  codice_fiscale: string | null;
  matricola: string | null;
  data_nascita: string | null;
  data_assunzione: string | null;
  data_cessazione: string | null;
  ruolo: string;
  attivo: boolean;
  costo_orario: string | null;
  note_personale: string | null;
  created_at: string;
}

export interface BustaPaga {
  id: number;
  anno: number;
  mese: number | null;
  tipo_mensilita: string;
  netto: string | null;
  totale_competenze: string | null;
  totale_trattenute: string | null;
  tfr_maturato: string | null;
  ore_ordinarie: string | null;
  retribuzione_oraria: string | null;
  quadra: boolean | null;
  documento_id: number | null;
}

export interface SchedaDipendente {
  utente: AnagraficaDipendente;
  totali: {
    buste: number;
    buste_da_verificare: number;
    totale_netto: string | null;
    totale_lordo: string | null;
    tfr_maturato: string | null;
    contributi_dipendente: string | null;
    irpef: string | null;
    ore: string | null;
    primo_anno: number | null;
    ultimo_anno: number | null;
  };
  perAnno: Array<{
    anno: number; buste: number; netto: string | null; lordo: string | null;
    tfr: string | null; ore: string | null; retribuzione_oraria: string | null;
  }>;
  buste: BustaPaga[];
  documenti: { totale: number; buste_paga: number; cud: number };
  turni: { turni: number; ore_timbrate: string };
}

export interface CalcoloCostoOrario {
  costoOrario: number | null;
  buste: Array<{ anno: number; mese: number | null; retribuzioneOraria: number }>;
  motivo: string | null;
}

export interface GustoBanco {
  id: number;
  nome: string;
  categoria: string;
  colore: string;
  descrizione: string | null;
  allergeni: string | null;
  stagionale: boolean;
  foto_url: string | null;
  kg_prodotti: string;
  kg_giacenza: string;
  /** true se l'ultimo conteggio di magazzino lo da' presente */
  in_magazzino: boolean;
  /** il giorno di quel conteggio: puo' essere ieri o la settimana scorsa */
  data_magazzino: string | null;
  /** la scelta fatta al banco: null se nessuno l'ha toccato */
  scelta_manuale: boolean | null;
  /** il giorno della scelta: vince sul magazzino solo se non e' piu' vecchia */
  data_scelta: string | null;
  aggiornato_at: string | null;
  aggiornato_da: string | null;
  /** quello che vale davvero, ed e' cio' che vedono monitor e sito */
  disponibile: boolean;
}

export interface EsitoBanco {
  puntoVenditaId?: number;
  data?: string;
  /** il giorno dell'inventario da cui parte la vetrina: non e' detto sia oggi */
  dataMagazzino?: string | null;
  inVetrina: number;
  gusti: GustoBanco[];
}

export interface PuntoVenditaBanco {
  id: number;
  nome: string;
}

export interface MediaMonitor {
  id: number;
  nome: string;
  tipo: 'immagine' | 'video';
  url: string;
  durata_secondi: string | null;
  dimensione_byte: string | null;
  created_at: string;
}

export interface PaginaMonitor {
  id: number;
  tipo: 'gusti' | 'media';
  titolo: string | null;
  /** Le categorie mostrate insieme in questa pagina. Vuoto o assente = tutte. */
  categorie: string[] | null;
  media_id: number | null;
  media_nome?: string | null;
  media_tipo?: 'immagine' | 'video' | null;
  media_url?: string | null;
  durata_secondi?: string | null;
  ordine: number;
  secondi: number | null;
  attiva: boolean;
}

export interface SchermoMonitor {
  id: number;
  nome: string;
  token: string;
  punto_vendita_id: number | null;
  punto_vendita_nome?: string | null;
  attivo: boolean;
  ultimo_accesso_at?: string | null;
  note?: string | null;
  secondi_pagina?: number;
  media_sfondo_id?: number | null;
  alterna_nomi?: boolean;
  secondi_nome?: number;
  gusti_per_pagina?: number;
  scala?: number;
  margine_alto?: number;
  margine_lati?: number;
  velo_sfondo?: number;
  /** Griglia fissata a mano: 0 vuol dire "calcolala tu". */
  colonne?: number;
  righe?: number;
  /** Distanza fra i gusti, in percentuale del valore di riferimento. */
  spazio_verticale?: number;
  spazio_orizzontale?: number;
}

export interface DocumentoPersonale {
  id: number;
  utente_id: number;
  tipo: string;
  anno: number | null;
  mese: number | null;
  titolo: string;
  nome_file_originale: string;
  dimensione_byte: number;
  caricato_at: string;
  visto_dal_dipendente_at: string | null;
  visibile_al_dipendente: boolean;
}

export interface VoceContoEconomico {
  codice: string;
  nome: string;
  totale: number;
  movimenti: number | null;
  fonte?: string;
  dettaglioCanali?: { canale: string; totale: number }[];
}

export interface ContoEconomico {
  periodo: { da: string; a: string; base: string };
  sezioni: Record<SezioneCe, VoceContoEconomico[]>;
  totali: {
    ricavi: number;
    costiVariabili: number;
    margineContribuzione: number;
    margineContribuzionePercentuale: number | null;
    costiFissi: number;
    ebitda: number;
    ebitdaPercentuale: number | null;
    sottoLinea: number;
    risultatoNetto: number;
  };
  daClassificare: { movimenti: number; valoreAssoluto: number };
}

export interface CeRiconciliazione {
  periodo: { da: string; a: string; codaBancaria: string };
  incassatoKiara: number;
  confronti: {
    canale: string;
    incassatoKiara: number;
    arrivatoInBanca: number | null;
    differenza: number | null;
    trattenutoPercentuale: number | null;
    confrontabile: boolean;
  }[];
  elettronico: {
    incassatoKiara: number;
    arrivatoInBanca: number;
    differenza: number;
    costoPercentuale: number | null;
    perFornitore: { fornitore: string | null; totale: number; movimenti: number }[];
  };
  contanti: { incassatoKiara: number; versatoInBanca: number };
  canaliNonMappati: { canale: string; totale: number }[];
}

export interface CategoriaMenu {
  id: number;
  nome: string;
  ordine: number;
}

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;
  private tokenKey: string; // Chiave per localStorage specifica per ambiente

  constructor() {
    // Determina l'URL del backend dinamicamente
    this.baseURL = this.getApiBaseUrl();

    // Chiave token specifica per ambiente per evitare conflitti
    this.tokenKey = this.baseURL.includes('localhost') || this.baseURL.includes('127.0.0.1')
      ? 'gelato_token_dev'
      : 'gelato_token_prod';

    // Migrazione automatica dalle chiavi legacy
    this.migrateLegacyTokens();

    console.log(`🔧 ApiService inizializzato - Ambiente: ${this.tokenKey}, URL: ${this.baseURL}`);

    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 120000, // 120 secondi per le chiamate AI
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor per aggiungere il token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem(this.tokenKey);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Interceptor per gestire errori
    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Token scaduto o non valido
          localStorage.removeItem(this.tokenKey);
          localStorage.removeItem(`${this.tokenKey.replace('_token', '_user')}`);
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Metodo per determinare l'URL del backend dinamicamente
  private getApiBaseUrl(): string {
    // Se è impostata la variabile d'ambiente, usala (preferito)
    if (process.env.REACT_APP_API_URL) {
      console.log('🌐 Usando API URL da variabile ambiente:', process.env.REACT_APP_API_URL);
      return process.env.REACT_APP_API_URL;
    }

    // Altrimenti, determina automaticamente basandosi su window.location
    const hostname = window.location.hostname;
    // PRODUZIONE: sistemagelato.omai.cloud
    if (hostname === 'sistemagelato.omai.cloud') {
      console.log('🌐 Ambiente di produzione rilevato, usando proxy API');
      return '/api';  // Usa il proxy di Apache
    }

    // SVILUPPO REMOTO: dev-sistemagelato.omai.cloud
    if (hostname === 'dev-sistemagelato.omai.cloud') {
      console.log('🌐 Ambiente di sviluppo remoto rilevato');
      return 'http://localhost:3001/api'; // Backend locale per sviluppo
    }

    // SVILUPPO LOCALE: localhost/127.0.0.1 - sempre usa localhost per il browser
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      console.log('🌐 Ambiente di sviluppo locale rilevato (proxy nginx)');
      return '/api'; // usa proxy del frontend container
    }

    // SVILUPPO MOBILE: IP della rete locale (192.168.x.x, 10.x.x.x, etc.)
    if (hostname.match(/^192\.168\./) || hostname.match(/^10\./) || hostname.match(/^172\.(1[6-9]|2[0-9]|3[01])\./) || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
      console.log('🌐 Ambiente di sviluppo mobile rilevato');
      return `http://${hostname}:3001/api`; // Usa l'IP host, non il nome container
    }

    // FALLBACK: stesso protocollo e hostname del frontend
    console.log('🌐 Usando fallback URL API');
    return '/api';
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await this.api.get('/health');
    return response.data;
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.api.post('/auth/login', credentials);
    return response.data;
  }

  async loginWithQr(token: string): Promise<LoginResponse> {
    const response = await this.api.post('/auth/login-qr', { token });
    return response.data;
  }

  async verifyToken(): Promise<{ user: User }> {
    const response = await this.api.get('/auth/verify');
    return response.data;
  }

  async logout(): Promise<void> {
    await this.api.post('/auth/logout');
  }

  async getQrToken(): Promise<{ token: string | null; qrCodeUrl?: string }> {
    const response = await this.api.get('/auth/qr-token');
    return response.data;
  }

  async regenerateQrToken(): Promise<{ token: string; qrCodeUrl: string }> {
    const response = await this.api.post('/auth/qr-token/regenerate');
    return response.data;
  }

  async revokeQrToken(): Promise<{ message: string }> {
    const response = await this.api.post('/auth/qr-token/revoke');
    return response.data;
  }

  async impersonate(userId: number): Promise<LoginResponse> {
    const response = await this.api.post('/auth/impersonate', { user_id: userId });
    return response.data;
  }

  // Gusti endpoints
  async getGusti(includiDisattivi = false): Promise<Gusto[]> {
    const response = await this.api.get('/gusti', {
      params: { includi_disattivi: includiDisattivi }
    });
    return response.data;
  }

  /** Carica o sostituisce la foto di un gusto. La precedente viene cancellata. */
  async caricaFotoGusto(gustoId: number, file: File): Promise<Gusto> {
    const formData = new FormData();
    formData.append('foto', file);
    const response = await this.api.post(`/gusti/${gustoId}/foto`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async rimuoviFotoGusto(gustoId: number): Promise<{ id: number; foto_url: null }> {
    const response = await this.api.delete(`/gusti/${gustoId}/foto`);
    return response.data;
  }

  async createGusto(gusto: Omit<Gusto, 'id' | 'created_at' | 'attivo'>): Promise<Gusto> {
    const response = await this.api.post('/gusti', gusto);
    return response.data;
  }

  async updateGusto(id: number, gusto: Partial<Gusto>): Promise<{ message: string, food_cost: number }> {
    const response = await this.api.put(`/gusti/${id}`, gusto);
    return response.data;
  }

  async deleteGusto(id: number): Promise<{ message: string, gusto: Gusto }> {
    const response = await this.api.delete(`/gusti/${id}`);
    return response.data;
  }
  async getAnalisiGiornaliera(startDate?: string, endDate?: string, puntoVenditaId?: number): Promise<any[]> {
    const response = await this.api.get('/forecasts/analisi-giornaliera', {
      params: { startDate, endDate, punto_vendita_id: puntoVenditaId }
    });
    return response.data;
  }

  async attivaGusto(id: number): Promise<{ message: string, gusto: Gusto }> {
    const response = await this.api.patch(`/gusti/${id}/attiva`);
    return response.data;
  }

  async disattivaGusto(id: number): Promise<{ message: string, gusto: Gusto }> {
    const response = await this.api.patch(`/gusti/${id}/disattiva`);
    return response.data;
  }

  async calcolaRicette(gustiSelezionati: { gusto_id: number; quantita_kg: number }[]): Promise<{
    dettagli_per_gusto: Array<{
      gusto_id: number;
      gusto_nome: string;
      gusto_colore: string;
      quantita_kg: number;
      ingredienti: Array<{
        ingrediente_id: number;
        ingrediente_nome: string;
        ingrediente_unita: string;
        ingrediente_costo: number;
        quantita_per_kg: number;
        unita_ricetta: string;
        quantita_necessaria: number;
        costo_ingrediente: number;
        is_variegatura: boolean;
        is_aggiunta: boolean;
      }>;
      costo_totale_gusto: number;
      costo_per_kg: number;
    }>;
    riepilogo_ingredienti: Array<{
      ingrediente_id: number;
      ingrediente_nome: string;
      ingrediente_unita: string;
      unita_ricetta: string;
      quantita_totale: number;
      costo_totale: number;
    }>;
    costo_totale_produzione: number;
    costo_medio_per_kg: number;
    kg_totali: number;
    num_gusti: number;
    num_ingredienti_diversi: number;
  }> {
    const response = await this.api.post('/gusti/calcola-ricette', {
      gusti_selezionati: gustiSelezionati
    });
    return response.data;
  }

  // Punti vendita endpoints
  async getPuntiVendita(): Promise<PuntoVendita[]> {
    const response = await this.api.get('/punti-vendita');
    return response.data;
  }

  async createPuntoVendita(puntoVendita: Omit<PuntoVendita, 'id' | 'created_at' | 'attivo'>): Promise<PuntoVendita> {
    const response = await this.api.post('/punti-vendita', puntoVendita);
    return response.data;
  }

  async updatePuntoVendita(id: number, puntoVendita: Partial<PuntoVendita>): Promise<PuntoVendita> {
    const response = await this.api.put(`/punti-vendita/${id}`, puntoVendita);
    return response.data.puntoVendita;
  }

  async deletePuntoVendita(id: number): Promise<{ message: string, puntoVendita: PuntoVendita }> {
    const response = await this.api.delete(`/punti-vendita/${id}`);
    return response.data;
  }

  // ============================================================
  // Categorie Menu endpoints
  // ============================================================
  async getCategorieMenu(): Promise<CategoriaMenu[]> {
    const response = await this.api.get('/menu/categorie');
    return response.data;
  }

  async creaCategoriaMenu(categoria: Omit<CategoriaMenu, 'id'>): Promise<CategoriaMenu> {
    const response = await this.api.post('/menu/categorie', categoria);
    return response.data;
  }

  async eliminaCategoriaMenu(id: number): Promise<{ message: string }> {
    const response = await this.api.delete(`/menu/categorie/${id}`);
    return response.data;
  }

  // Produzione endpoints
  async getProduzione(params?: { data?: string; puntoVenditaId?: number }): Promise<Produzione[]> {
    const response = await this.api.get('/produzione', { params });
    return response.data;
  }

  async createProduzione(produzione: CreaProduzionRequest): Promise<{ id: number; message: string; kg_totale: number }> {
    const response = await this.api.post('/produzione', produzione);
    return response.data;
  }

  async updateProduzione(id: number, produzione: Partial<CreaProduzionRequest>): Promise<{ message: string, id: number, kg_totale: number, produzione: Produzione }> {
    const response = await this.api.put(`/produzione/${id}`, produzione);
    return response.data;
  }

  async deleteProduzione(id: number): Promise<{ message: string, id: number, produzione: Produzione }> {
    try {
      const response = await this.api.delete(`/produzione/${id}`);

      // Log per debugging
      console.log('Risposta eliminazione produzione:', response.data);

      // Verifica che la risposta contenga i dati necessari
      if (!response.data || !response.data.id) {
        throw new Error('Risposta del server incompleta per l\'eliminazione della produzione');
      }

      return response.data;
    } catch (error) {
      console.error('Errore API deleteProduzioni:', error);
      throw error;
    }
  }

  // Users endpoints
  async getUsers(): Promise<User[]> {
    const response = await this.api.get('/users');
    return response.data;
  }

  // ============================================================
  // Giacenza endpoints
  // ============================================================
  async getGiacenza(params: { data: string; puntoVenditaId: number }): Promise<Giacenza | null> {
    const response = await this.api.get('/giacenza', { params });
    return response.data;
  }

  async getStockCumulativo(params: { data: string; puntoVenditaId: number }): Promise<StockCumulativo> {
    const response = await this.api.get('/giacenza/stock-cumulativo', { params });
    return response.data;
  }

  async getUltimaGiacenzaPrecedente(params: { data: string; puntoVenditaId: number }): Promise<Giacenza | null> {
    const response = await this.api.get('/giacenza/ultima-precedente', { params });
    return response.data;
  }

  async saveGiacenza(giacenza: CreaGiacenzaRequest): Promise<{ id: number; message: string; kg_totale: number; gusti_count: number }> {
    const response = await this.api.post('/giacenza', giacenza);
    return response.data;
  }

  async getStoricoGiacenza(params?: { startDate?: string; endDate?: string; puntoVenditaId?: number }): Promise<Giacenza[]> {
    const response = await this.api.get('/giacenza/storico', { params });
    return response.data;
  }

  // ============================================================
  // Smaltimento endpoints
  // ============================================================
  async getSmaltimento(params: { data: string; puntoVenditaId: number }): Promise<Smaltimento | null> {
    const response = await this.api.get('/smaltimento', { params });
    return response.data;
  }

  async getStockDisponibile(params: { data: string; puntoVenditaId: number }): Promise<{ data: string; puntoVenditaId: number; stock: StockDisponibileItem[] }> {
    const response = await this.api.get('/smaltimento/stock-disponibile', { params });
    return response.data;
  }

  async saveSmaltimento(smaltimento: CreaSmaltimentoRequest): Promise<{ id: number; message: string; kg_totale: number; gusti_count: number }> {
    const response = await this.api.post('/smaltimento', smaltimento);
    return response.data;
  }

  async getStoricoSmaltimento(params?: { startDate?: string; endDate?: string; puntoVenditaId?: number }): Promise<Smaltimento[]> {
    const response = await this.api.get('/smaltimento/storico', { params });
    return response.data;
  }

  async createUser(user: Omit<User, 'id' | 'created_at' | 'updated_at'> & { password: string }): Promise<User> {
    const response = await this.api.post('/users', user);
    return response.data;
  }

  async updateUser(id: number, user: Partial<User & { password?: string }>): Promise<void> {
    await this.api.put(`/users/${id}`, user);
  }

  async deleteUser(id: number): Promise<{ message: string, id: number }> {
    const response = await this.api.delete(`/users/${id}`);
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.api.put('/users/me/password', { currentPassword, newPassword });
  }

  async getUserQrToken(userId: number): Promise<{ token: string | null; qrCodeUrl?: string }> {
    const response = await this.api.get(`/users/${userId}/qr-token`);
    return response.data;
  }

  async regenerateUserQrToken(userId: number): Promise<{ token: string; qrCodeUrl: string }> {
    const response = await this.api.post(`/users/${userId}/qr-token/regenerate`);
    return response.data;
  }

  // Analytics endpoints
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await this.api.get('/analytics/dashboard');
    return response.data;
  }

  async getCostoTotale(anno?: number, mese?: number): Promise<{ costo_totale: number, anno: number, mese: number }> {
    const response = await this.api.get('/analytics/costo-totale', {
      params: { anno, mese }
    });
    return response.data;
  }

  async getFoodCostGusti(): Promise<any[]> {
    const response = await this.api.get('/analytics/food-cost-gusti');
    return response.data;
  }

  async getCostoProduzioni(anno?: number, mese?: number): Promise<any[]> {
    const response = await this.api.get('/analytics/costo-produzioni', {
      params: { anno, mese }
    });
    return response.data;
  }

  async getDettaglioCostoProduzione(id: number): Promise<any> {
    const response = await this.api.get(`/analytics/dettaglio-costo-produzione/${id}`);
    return response.data;
  }

  async getStatsPeriodo(filtri: FiltriAnalytics): Promise<any[]> {
    const response = await this.api.get('/analytics/periodo', { params: filtri });
    return response.data;
  }

  async getStatsGusto(gustoId: number, filtri?: FiltriAnalytics): Promise<any[]> {
    const response = await this.api.get(`/analytics/gusto/${gustoId}`, { params: filtri });
    return response.data;
  }

  async getTrendMensile(anno?: number): Promise<TrendMensile[]> {
    const response = await this.api.get('/analytics/trend-mensile', { params: { anno } });
    return response.data;
  }

  async exportData(filtri: FiltriAnalytics): Promise<any> {
    const response = await this.api.get('/analytics/export', { params: { ...filtri, formato: 'excel' } });
    return response.data;
  }

  // Nuove API Analytics avanzate
  async getAnalyticsGusti(filtri: FiltriAnalytics): Promise<AnalyticsGusto[]> {
    const response = await this.api.get('/analytics/gusti', { params: filtri });
    return response.data;
  }

  async getAnalyticsIngredienti(filtri: FiltriAnalytics): Promise<AnalyticsIngrediente[]> {
    const response = await this.api.get('/analytics/ingredienti-avanzate', { params: filtri });
    return response.data;
  }

  async getAnalyticsPeriodo(filtri: FiltriAnalytics): Promise<AnalyticsPeriodo[]> {
    const response = await this.api.get('/analytics/periodo-dettagli', { params: filtri });
    return response.data;
  }

  async getAnalyticsConfronto(filtri: FiltriAnalytics): Promise<AnalyticsConfronto> {
    const response = await this.api.get('/analytics/confronto', { params: filtri });
    return response.data;
  }

  async getTopGusti(filtri: FiltriAnalytics, limite: number = 10): Promise<AnalyticsGusto[]> {
    const response = await this.api.get('/analytics/top-gusti', { params: { ...filtri, limite } });
    return response.data;
  }

  async getCostiProduzione(filtri: FiltriAnalytics): Promise<any> {
    const response = await this.api.get('/analytics/costi', { params: filtri });
    return response.data;
  }

  async getGustiRedditizi(filtri: FiltriAnalytics, limite: number = 10): Promise<AnalyticsFoodCostGusti> {
    const response = await this.api.get('/analytics/gusti-redditizi', { params: { ...filtri, limit: limite } });
    return response.data;
  }

  // Config endpoints
  async getConfigurazione(): Promise<Configurazione[]> {
    const response = await this.api.get('/config');
    return response.data;
  }

  async getConfigurazioneByKey(chiave: string): Promise<Configurazione> {
    const response = await this.api.get(`/config/${chiave}`);
    return response.data;
  }

  async updateConfigurazione(chiave: string, valore: string, descrizione?: string): Promise<void> {
    await this.api.put(`/config/${chiave}`, { valore, descrizione });
  }

  async deleteConfigurazione(chiave: string): Promise<void> {
    await this.api.delete(`/config/${chiave}`);
  }

  async getLogAttivita(params?: { limite?: number; offset?: number }): Promise<LogAttivita[]> {
    const response = await this.api.get('/config/logs/attivita', { params });
    return response.data;
  }

  async deleteLogAttivita(id: number): Promise<void> {
    await this.api.delete(`/config/logs/attivita/${id}`);
  }

  // INGREDIENTI API
  async getIngredienti(includiDisattivi = false): Promise<Ingrediente[]> {
    const response = await this.api.get('/ingredienti', {
      params: { includi_disattivi: includiDisattivi }
    });
    return response.data;
  }

  async getIngrediente(id: number): Promise<Ingrediente> {
    const response = await this.api.get(`/ingredienti/${id}`);
    return response.data;
  }

  async creaIngrediente(ingrediente: Omit<Ingrediente, 'id' | 'created_at' | 'updated_at'>): Promise<Ingrediente> {
    const response = await this.api.post('/ingredienti', ingrediente);
    return response.data;
  }

  async aggiornaIngrediente(id: number, ingrediente: Partial<Omit<Ingrediente, 'id' | 'created_at' | 'updated_at'>>): Promise<Ingrediente> {
    const response = await this.api.put(`/ingredienti/${id}`, ingrediente);
    return response.data;
  }

  async eliminaIngrediente(id: number): Promise<{ message: string, ingrediente: Ingrediente }> {
    const response = await this.api.delete(`/ingredienti/${id}`);
    return response.data;
  }

  async attivaIngrediente(id: number): Promise<{ message: string, ingrediente: Ingrediente }> {
    const response = await this.api.patch(`/ingredienti/${id}/attiva`);
    return response.data;
  }

  async disattivaIngrediente(id: number): Promise<{ message: string, ingrediente: Ingrediente }> {
    const response = await this.api.patch(`/ingredienti/${id}/disattiva`);
    return response.data;
  }

  async getStatisticheIngredienti(dataInizio?: string, dataFine?: string): Promise<StatisticheIngredientiResponse> {
    const params: any = {};
    if (dataInizio) params.data_inizio = dataInizio;
    if (dataFine) params.data_fine = dataFine;

    const response = await this.api.get('/ingredienti/stats', { params });
    return response.data;
  }

  // === RICETTE API (NUOVE) ===
  async getRicettaGusto(gustoId: number): Promise<{
    gusto: { id: number; nome: string };
    ingredienti: Array<{
      ingrediente_id: number;
      nome: string;
      quantita: number;
      unita: string;
      prezzo_per_kg: number;
      unita_misura: string;
      fornitore: string;
      costo_ingrediente: number;
    }>;
    costo_totale: number;
    totale_ingredienti: number;
  }> {
    const response = await this.api.get(`/ricette/gusto/${gustoId}`);
    return response.data;
  }

  async updateRicettaGusto(gustoId: number, ingredienti: Array<{
    ingrediente_id: number;
    quantita: number;
    unita: string;
  }>): Promise<{
    message: string;
    gusto: string;
    ingredienti_salvati: number;
    food_cost: number;
  }> {
    const response = await this.api.put(`/ricette/gusto/${gustoId}`, { ingredienti });
    return response.data;
  }

  async getTutteLeRicette(): Promise<Array<{
    gusto_id: number;
    gusto_nome: string;
    categoria: string;
    ingredienti: Array<{
      ingrediente_id: number;
      nome: string;
      quantita: number;
      unita: string;
      prezzo_per_kg: number;
      costo_ingrediente: number;
    }>;
    costo_totale: number;
  }>> {
    const response = await this.api.get('/ricette');
    return response.data;
  }

  // CATEGORIE GUSTI API
  async getCategorieGusti(): Promise<CategoriaGusto[]> {
    const response = await this.api.get('/config/categorie');
    return response.data;
  }

  async getCategoriaGusto(id: number): Promise<CategoriaGusto> {
    const response = await this.api.get(`/config/categorie/${id}`);
    return response.data;
  }

  async creaCategoriaGusto(categoria: Omit<CategoriaGusto, 'id' | 'created_at' | 'updated_at'>): Promise<CategoriaGusto> {
    const response = await this.api.post('/config/categorie', categoria);
    return response.data;
  }

  async aggiornaCategoriaGusto(id: number, categoria: Partial<Omit<CategoriaGusto, 'id' | 'created_at' | 'updated_at'>>): Promise<CategoriaGusto> {
    try {
      const response = await this.api.put(`/config/categorie/${id}`, categoria);
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async eliminaCategoriaGusto(id: number): Promise<void> {
    try {
      const response = await this.api.delete(`/config/categorie/${id}`);
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  // ALLERGENI API
  async getAllergeni(): Promise<Allergene[]> {
    const response = await this.api.get('/config/allergeni');
    return response.data;
  }

  async getAllergene(id: number): Promise<Allergene> {
    const response = await this.api.get(`/config/allergeni/${id}`);
    return response.data;
  }

  async creaAllergene(allergene: Omit<Allergene, 'id' | 'created_at' | 'updated_at'>): Promise<Allergene> {
    const response = await this.api.post('/config/allergeni', allergene);
    return response.data;
  }

  async aggiornaAllergene(id: number, allergene: Partial<Omit<Allergene, 'id' | 'created_at' | 'updated_at'>>): Promise<Allergene> {
    try {
      const response = await this.api.put(`/config/allergeni/${id}`, allergene);
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async eliminaAllergene(id: number): Promise<void> {
    try {
      const response = await this.api.delete(`/config/allergeni/${id}`);
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  // Admin endpoints
  async getAdminStats(): Promise<any> {
    const response = await this.api.get('/admin/stats');
    return response.data;
  }

  async resetDatabase(password: string): Promise<any> {
    const response = await this.api.post('/admin/reset-database', { password });
    return response.data;
  }

  async downloadBackup(): Promise<any> {
    const response = await this.api.get('/admin/backup');
    return response.data;
  }

  async importBackup(backupFile: File): Promise<any> {
    const formData = new FormData();
    formData.append('backupFile', backupFile);

    const response = await this.api.post('/admin/import-backup', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async runKiaraTestExport(): Promise<any> {
    const response = await this.api.post('/admin/kiara-test-export');
    return response.data;
  }

  async downloadKiaraExport(fileName: string): Promise<Blob> {
    const safeName = encodeURIComponent(fileName);
    const response = await this.api.get(`/admin/kiara-exports/${safeName}`, {
      responseType: 'blob'
    });
    return response.data;
  }

  async getKiaraImportRuns(limit = 30): Promise<any> {
    const response = await this.api.get('/admin/kiara-imports', {
      params: { limit }
    });
    return response.data;
  }

  async retryKiaraImportRun(runId: number): Promise<any> {
    const response = await this.api.post(`/admin/kiara-imports/${runId}/retry`);
    return response.data;
  }

  async runKiaraImportForDate(date: string): Promise<any> {
    const response = await this.api.post('/admin/kiara-imports/run', { date });
    return response.data;
  }

  async getKiaraDatiVenduto(dateFrom: string, dateTo: string): Promise<any> {
    const response = await this.api.get('/admin/kiara-data/venduto', {
      params: { date_from: dateFrom, date_to: dateTo }
    });
    return response.data;
  }

  async getKiaraDatiTestate(dateFrom: string, dateTo: string): Promise<any> {
    const response = await this.api.get('/admin/kiara-data/testate', {
      params: { date_from: dateFrom, date_to: dateTo }
    });
    return response.data;
  }

  async getKiaraStatsTotali(dateFrom: string, dateTo: string): Promise<any> {
    const response = await this.api.get('/admin/kiara-stats/totali', {
      params: { date_from: dateFrom, date_to: dateTo }
    });
    return response.data;
  }

  async getKiaraStatsProdotti(dateFrom: string, dateTo: string): Promise<any> {
    const response = await this.api.get('/admin/kiara-stats/prodotti', {
      params: { date_from: dateFrom, date_to: dateTo }
    });
    return response.data;
  }

  async getKiaraStatsPagamenti(dateFrom: string, dateTo: string): Promise<any> {
    const response = await this.api.get('/admin/kiara-stats/pagamenti', {
      params: { date_from: dateFrom, date_to: dateTo }
    });
    return response.data;
  }

  async getKiaraVersamenti(): Promise<any> {
    const response = await this.api.get('/admin/kiara-versamenti');
    return response.data;
  }

  async setKiaraVersamentoFlag(weekStart: string, tipo: 'preparato' | 'prelevato', importo?: number): Promise<any> {
    const response = await this.api.post('/admin/kiara-versamenti/flag', { week_start: weekStart, tipo, importo });
    return response.data;
  }

  // Test di connessione API
  async testApiConnection(): Promise<boolean> {
    try {
      const response = await this.api.get('/health');
      return response.status === 200;
    } catch (error) {
      return false;
    }
  }

  // Metodi per la gestione delle email

  /**
   * Recupera la configurazione email corrente
   */
  async getEmailConfig(): Promise<any> {
    try {
      const response = await this.api.get('/email/config');
      return response.data;
    } catch (error) {
      console.error('Errore API getEmailConfig:', error);
      throw error;
    }
  }

  /**
   * Aggiorna la configurazione email
   */
  async updateEmailConfig(configs: any[]): Promise<any> {
    try {
      const response = await this.api.post('/email/config', { configs });
      return response.data;
    } catch (error) {
      console.error('Errore API updateEmailConfig:', error);
      throw error;
    }
  }

  /**
   * Invia un'email di test
   */
  async sendTestEmail(email: string): Promise<any> {
    try {
      const response = await this.api.post('/email/test', { email });
      return response.data;
    } catch (error) {
      console.error('Errore API sendTestEmail:', error);
      throw error;
    }
  }

  /**
   * Invia manualmente l'email di notifica per una produzione specifica
   */
  async sendEmailForProduction(produzioneId: number): Promise<any> {
    try {
      const response = await this.api.post(`/email/produzione/${produzioneId}`);
      return response.data;
    } catch (error) {
      console.error('Errore API sendEmailForProduction:', error);
      throw error;
    }
  }

  /**
   * Recupera lo storico delle email
   */
  async getEmailLog(filters?: { tipo?: string, stato?: string, limit?: number, offset?: number }): Promise<any> {
    try {
      const response = await this.api.get('/email/log', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Errore API getEmailLog:', error);
      throw error;
    }
  }

  /**
   * Recupera i dettagli di una email specifica
   */
  async getEmailDetails(id: number): Promise<any> {
    try {
      const response = await this.api.get(`/email/log/${id}`);
      return response.data;
    } catch (error) {
      console.error('Errore API getEmailDetails:', error);
      throw error;
    }
  }

  // ===== METEO API =====

  /**
   * Ottieni meteo corrente per un punto vendita
   */
  async getCurrentWeather(puntoVenditaId: number): Promise<any> {
    try {
      const response = await this.api.get(`/weather/current/${puntoVenditaId}`);
      // Estrai i dati meteo dalla risposta dell'API
      if (response.data.success && response.data.data.meteo) {
        return response.data.data.meteo;
      }
      throw new Error('Formato risposta API non valido');
    } catch (error) {
      console.error('Errore API getCurrentWeather:', error);
      throw error;
    }
  }

  /**
   * Ottieni previsioni meteo per un punto vendita
   */
  async getWeatherForecast(puntoVenditaId: number): Promise<any> {
    try {
      const response = await this.api.get(`/weather/forecast/${puntoVenditaId}`);
      // Estrai le previsioni dalla risposta dell'API
      if (response.data.success && response.data.data.previsioni) {
        return response.data.data.previsioni;
      }
      throw new Error('Formato risposta API non valido');
    } catch (error) {
      console.error('Errore API getWeatherForecast:', error);
      throw error;
    }
  }

  /**
   * Ottieni storico meteo per un punto vendita
   */
  async getWeatherHistory(
    puntoVenditaId: number,
    dataInizio?: string,
    dataFine?: string
  ): Promise<any> {
    try {
      const params = new URLSearchParams();
      if (dataInizio) params.append('dataInizio', dataInizio);
      if (dataFine) params.append('dataFine', dataFine);

      const response = await this.api.get(
        `/weather/history/${puntoVenditaId}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error('Errore API getWeatherHistory:', error);
      throw error;
    }
  }

  /**
   * Ottieni analytics meteo e correlazioni con produzione
   */
  async getWeatherAnalytics(
    puntoVenditaId: number,
    mese?: number,
    anno?: number
  ): Promise<any> {
    try {
      const params = new URLSearchParams();
      if (mese) params.append('mese', mese.toString());
      if (anno) params.append('anno', anno.toString());

      const response = await this.api.get(
        `/weather/analytics/${puntoVenditaId}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error('Errore API getWeatherAnalytics:', error);
      throw error;
    }
  }

  /**
   * Aggiorna meteo per tutti i punti vendita (solo admin)
   */
  async updateAllWeather(): Promise<any> {
    try {
      const response = await this.api.post('/weather/update-all');
      return response.data;
    } catch (error) {
      console.error('Errore API updateAllWeather:', error);
      throw error;
    }
  }

  /**
   * Ottieni configurazioni meteo (solo admin)
   */
  async getWeatherConfig(): Promise<any> {
    try {
      const response = await this.api.get('/weather/config');
      return response.data;
    } catch (error) {
      console.error('Errore API getWeatherConfig:', error);
      throw error;
    }
  }

  /**
   * Aggiorna configurazioni meteo (solo admin)
   */
  async updateWeatherConfig(config: {
    apiKey?: string;
    aggiornamentoAutomatico?: boolean;
    intervalloOre?: number;
    provider?: string;
  }): Promise<any> {
    try {
      const response = await this.api.put('/weather/config', config);
      return response.data;
    } catch (error) {
      console.error('Errore API updateWeatherConfig:', error);
      throw error;
    }
  }

  /**
   * Test connessione API meteo (solo admin)
   */
  async testWeatherConnection(): Promise<any> {
    try {
      const response = await this.api.post('/weather/test');
      return response.data;
    } catch (error) {
      console.error('Errore API testWeatherConnection:', error);
      throw error;
    }
  }

  // === PREVISIONI AI ===
  async getDailyForecast(puntoVenditaId: number, dataProduzione: string): Promise<any> {
    try {
      const response = await this.api.post('/forecasts/daily', {
        punto_vendita_id: puntoVenditaId,
        data_produzione: dataProduzione
      });
      return response.data;
    } catch (error) {
      console.error('Errore API getDailyForecast:', error);
      throw error;
    }
  }

  async getAnnualForecast(puntoVenditaId: number, anno: number): Promise<any> {
    try {
      const response = await this.api.post('/forecasts/annual', {
        punto_vendita_id: puntoVenditaId,
        anno: anno,
        use_v2: false // V1: sincrono
      });
      return response.data;
    } catch (error) {
      console.error('Errore API getAnnualForecast:', error);
      throw error;
    }
  }

  async generateAnnualForecastV2(puntoVenditaId: number, anno: number): Promise<any> {
    try {
      const response = await this.api.post('/forecasts/annual', {
        punto_vendita_id: puntoVenditaId,
        anno: anno,
        use_v2: true // V2: asincrono con reasoning
      });
      return response.data;
    } catch (error) {
      console.error('Errore API generateAnnualForecastV2:', error);
      throw error;
    }
  }

  // Metodo generico POST per chiamate custom
  async post(endpoint: string, data: any): Promise<any> {
    try {
      const response = await this.api.post(endpoint, data);
      return response.data;
    } catch (error) {
      console.error(`Errore API POST ${endpoint}:`, error);
      throw error;
    }
  }

  async getAnnualForecastDetails(id: number): Promise<any> {
    try {
      const response = await this.api.get(`/forecasts/annual/${id}`);
      return response.data;
    } catch (error) {
      console.error('Errore API getAnnualForecastDetails:', error);
      throw error;
    }
  }

  async getAnnualForecastList(anno?: number): Promise<any> {
    try {
      const url = anno ? `/forecasts/annual/list/${anno}` : '/forecasts/annual/list';
      const response = await this.api.get(url);
      return response.data;
    } catch (error) {
      console.error('Errore API getAnnualForecastList:', error);
      throw error;
    }
  }

  async getForecastAccuracy(previsioneId: number): Promise<any> {
    try {
      const response = await this.api.get(`/forecasts/accuracy/${previsioneId}`);
      return response.data;
    } catch (error) {
      console.error('Errore API getForecastAccuracy:', error);
      throw error;
    }
  }

  // === CALENDARIO ===
  async getCalendarioMese(anno: number, mese: number, puntoVenditaId?: number) {
    try {
      const params: any = { anno, mese };
      if (puntoVenditaId) {
        params.puntoVenditaId = puntoVenditaId;
      }

      const response = await this.api.get('/calendario/mese', { params });
      return response.data;
    } catch (error) {
      console.error('Errore API getCalendarioMese:', error);
      throw error;
    }
  }

  async getStatisticheMese(anno: number, mese: number, puntoVenditaId?: number) {
    try {
      const params: any = { anno, mese };
      if (puntoVenditaId) {
        params.puntoVenditaId = puntoVenditaId;
      }

      const response = await this.api.get('/calendario/statistiche-mese', { params });
      return response.data;
    } catch (error) {
      console.error('Errore API getStatisticheMese:', error);
      throw error;
    }
  }

  // Utility methods
  setAuthToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  removeAuthToken(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(`${this.tokenKey.replace('_token', '_user')}`);
  }

  getAuthToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }

  // Migrazione automatica dai token legacy alle nuove chiavi specifiche per ambiente
  private migrateLegacyTokens(): void {
    const legacyToken = localStorage.getItem('gelato_token');
    const legacyUser = localStorage.getItem('gelato_user');

    if (legacyToken && !localStorage.getItem(this.tokenKey)) {
      console.log('🔄 Migrazione token legacy in corso...');
      localStorage.setItem(this.tokenKey, legacyToken);
      localStorage.setItem(this.getUserKey(), legacyUser || '');

      // Rimuovi le chiavi legacy per evitare confusione
      localStorage.removeItem('gelato_token');
      localStorage.removeItem('gelato_user');

      console.log(`✅ Token migrato da legacy a ${this.tokenKey}`);
    }
  }

  // Metodo per ottenere la chiave user specifica per ambiente
  getUserKey(): string {
    return `${this.tokenKey.replace('_token', '_user')}`;
  }

  // Tenant registration endpoint
  async registerTenant(registrationData: {
    tenant_name: string;
    punto_vendita_nome: string;
    punto_vendita_indirizzo: string;
    admin_name: string;
    admin_email: string;
    admin_password: string;
  }): Promise<any> {
    const response = await this.api.post('/tenants/register', registrationData);
    return response.data;
  }

  // Tracciamento Analytics Base
  async trackPage(page: string): Promise<void> {
    try {
      await this.api.post('/auth/track-page', { page });
    } catch (err) {
      // Ignoriamo gli errori di tracciamento silenziosamente per non interrompere l'UX
      console.warn('Errore durante il tracciamento della pagina:', err);
    }
  }

  // Incasso
  async getIncassoToday(): Promise<any> {
    const response = await this.api.get('/incasso/today');
    return response.data;
  }

  async getIncassoSummary(): Promise<any> {
    const response = await this.api.get('/incasso/summary');
    return response.data;
  }

  async importIncassoNow(): Promise<any> {
    const response = await this.api.post('/incasso/import-now');
    return response.data;
  }

  // ============================================================
  // Controllo di gestione economico
  // ============================================================
  async getCeConti(): Promise<CeConto[]> {
    const response = await this.api.get('/controllo-gestione/conti');
    return response.data;
  }

  async creaCeConto(dati: {
    nome: string; tipo?: string; formatoImport?: string; iban?: string; note?: string;
  }): Promise<CeConto> {
    const response = await this.api.post('/controllo-gestione/conti', dati);
    return response.data;
  }

  /**
   * Cerca i pagamenti entrati due volte da conti diversi.
   * Senza applica=true si limita a dire cosa troverebbe.
   */
  async duplicatiFraConti(applica = false): Promise<CeEsitoDuplicati> {
    const response = await this.api.post('/controllo-gestione/duplicati-fra-conti', { applica });
    return response.data;
  }

  /** Accetta piu' file: il backend li elabora uno alla volta, in sequenza. */
  async importaEstrattoConto(contoId: number, files: File[]): Promise<CeEsitoImport> {
    const formData = new FormData();
    files.forEach((f) => formData.append('file', f));
    const response = await this.api.post(`/controllo-gestione/import/${contoId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async importaDelegheF24(files: File[], codiceFiscaleAzienda?: string): Promise<CeEsitoDeleghe> {
    const formData = new FormData();
    files.forEach((f) => formData.append('file', f));
    if (codiceFiscaleAzienda) formData.append('codiceFiscaleAzienda', codiceFiscaleAzienda);
    const response = await this.api.post('/controllo-gestione/f24/deleghe', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async analizzaQuietanzeF24(files: File[]): Promise<CeEsitoQuietanze> {
    const formData = new FormData();
    files.forEach((f) => formData.append('file', f));
    const response = await this.api.post('/controllo-gestione/f24/quietanze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async analizzaOrdiniAmazon(files: File[]): Promise<CeEsitoAmazon> {
    const formData = new FormData();
    files.forEach((f) => formData.append('file', f));
    const response = await this.api.post('/controllo-gestione/amazon/ordini', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  // ============================================================
  // Personale: documenti e timbrature
  // ============================================================
  async getMediaMonitor(): Promise<MediaMonitor[]> {
    const response = await this.api.get('/monitor/media');
    return response.data;
  }

  /** Carica un'immagine o un video: la conversione avviene sul server. */
  async caricaMediaMonitor(file: File, nome?: string): Promise<MediaMonitor> {
    const formData = new FormData();
    formData.append('file', file);
    if (nome) formData.append('nome', nome);
    const response = await this.api.post('/monitor/media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // La conversione di un video lungo puo' richiedere minuti
      timeout: 15 * 60 * 1000
    });
    return response.data;
  }

  async eliminaMediaMonitor(id: number): Promise<{ id: number }> {
    const response = await this.api.delete(`/monitor/media/${id}`);
    return response.data;
  }

  async getPagineMonitor(schermoId: number): Promise<PaginaMonitor[]> {
    const response = await this.api.get(`/monitor/schermi/${schermoId}/pagine`);
    return response.data;
  }

  async creaPaginaMonitor(schermoId: number, dati: {
    tipo: 'gusti' | 'media'; titolo?: string; categorie?: string[] | null;
    mediaId?: number | null; secondi?: number | null;
  }): Promise<PaginaMonitor> {
    const response = await this.api.post(`/monitor/schermi/${schermoId}/pagine`, dati);
    return response.data;
  }

  async aggiornaPaginaMonitor(pageId: number, dati: Partial<{
    titolo: string; categorie: string[]; mediaId: number | null;
    secondi: number | null; ordine: number; attiva: boolean;
  }>): Promise<PaginaMonitor> {
    const response = await this.api.put(`/monitor/pagine/${pageId}`, dati);
    return response.data;
  }

  async eliminaPaginaMonitor(pageId: number): Promise<{ id: number }> {
    const response = await this.api.delete(`/monitor/pagine/${pageId}`);
    return response.data;
  }

  async getCategorieMonitor(): Promise<string[]> {
    const response = await this.api.get('/monitor/categorie');
    return response.data;
  }

  async getSchermiMonitor(): Promise<SchermoMonitor[]> {
    const response = await this.api.get('/monitor/schermi');
    return response.data;
  }

  async creaSchermoMonitor(dati: {
    nome: string; puntoVenditaId: number; note?: string;
  }): Promise<SchermoMonitor> {
    const response = await this.api.post('/monitor/schermi', dati);
    return response.data;
  }

  async aggiornaSchermoMonitor(
    id: number, dati: Partial<{
      nome: string; puntoVenditaId: number; attivo: boolean; note: string;
      secondiPagina: number; mediaSfondoId: number | null;
      alternaNomi: boolean; secondiNome: number;
      gustiPerPagina: number; scala: number; margineAlto: number; margineLati: number;
      veloSfondo: number; colonne: number; righe: number;
      spazioVerticale: number; spazioOrizzontale: number;
    }>
  ): Promise<SchermoMonitor> {
    const response = await this.api.put(`/monitor/schermi/${id}`, dati);
    return response.data;
  }

  /** Rigenera il token: l'indirizzo precedente smette di funzionare subito. */
  async rigeneraTokenSchermo(id: number): Promise<SchermoMonitor> {
    const response = await this.api.post(`/monitor/schermi/${id}/rigenera-token`);
    return response.data;
  }

  async eliminaSchermoMonitor(id: number): Promise<{ id: number }> {
    const response = await this.api.delete(`/monitor/schermi/${id}`);
    return response.data;
  }

  async getGustiBanco(puntoVenditaId?: number, data?: string): Promise<EsitoBanco> {
    const response = await this.api.get('/banco', { params: { puntoVenditaId, data } });
    return response.data;
  }

  /** Accende o spegne un gusto. Risponde con l'elenco gia' aggiornato. */
  async impostaGustoBanco(
    gustoId: number, disponibile: boolean, puntoVenditaId?: number
  ): Promise<EsitoBanco> {
    const response = await this.api.put(`/banco/${gustoId}`, { disponibile, puntoVenditaId });
    return response.data;
  }

  /** Toglie la scelta manuale: torna a valere produzione e giacenza. */
  async azzeraGustoBanco(gustoId: number, puntoVenditaId?: number): Promise<EsitoBanco> {
    const response = await this.api.delete(`/banco/${gustoId}`, { params: { puntoVenditaId } });
    return response.data;
  }

  async getPuntiVenditaBanco(): Promise<PuntoVenditaBanco[]> {
    const response = await this.api.get('/banco/punti-vendita');
    return response.data;
  }

  // --- Fatture elettroniche ------------------------------------------------

  async importaFattureElettroniche(files: File[]): Promise<CeEsitoImportFatture> {
    const modulo = new FormData();
    files.forEach((file) => modulo.append('file', file));
    const response = await this.api.post('/controllo-gestione/fatture/import', modulo, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async getFatture(limite = 200): Promise<CeFattura[]> {
    const response = await this.api.get('/controllo-gestione/fatture', { params: { limite } });
    return response.data.fatture;
  }

  async getRigheFatturaDaAbbinare(fornitoreId?: number): Promise<RigaFatturaDaAbbinare[]> {
    const response = await this.api.get('/controllo-gestione/fatture/righe-da-abbinare', {
      params: { fornitoreId }
    });
    return response.data.righe;
  }

  /** ingredienteId null significa "questa voce non e' un ingrediente". */
  async abbinaRigaFattura(dati: {
    fornitoreId: number | null;
    codiceArticolo: string | null;
    descrizione: string;
    ingredienteId: number | null;
  }): Promise<{ righeAggiornate: number }> {
    const response = await this.api.post('/controllo-gestione/fatture/abbina', dati);
    return response.data;
  }

  async getPrezziDalleFatture(
    da?: string, a?: string
  ): Promise<{ periodo: { da: string; a: string }; prezzi: PrezzoDalleFatture[] }> {
    const response = await this.api.get('/controllo-gestione/fatture/prezzi', { params: { da, a } });
    return response.data;
  }

  async attribuisciFattura(id: number, fornitoreId: number): Promise<CeFattura> {
    const response = await this.api.put(`/controllo-gestione/fatture/${id}/fornitore`, { fornitoreId });
    return response.data.fattura;
  }

  // --- Fornitori -----------------------------------------------------------

  async getFornitori(opzioni?: { soloAttivi?: boolean; soloIngredienti?: boolean }): Promise<CeFornitore[]> {
    const response = await this.api.get('/fornitori', {
      params: {
        soloAttivi: opzioni?.soloAttivi ? 'true' : undefined,
        soloIngredienti: opzioni?.soloIngredienti ? 'true' : undefined
      }
    });
    return response.data.fornitori;
  }

  async getSchedaFornitore(id: number, da?: string, a?: string): Promise<SchedaFornitore> {
    const response = await this.api.get(`/fornitori/${id}`, { params: { da, a } });
    return response.data;
  }

  async creaFornitore(dati: Partial<CeFornitore>): Promise<CeFornitore> {
    const response = await this.api.post('/fornitori', dati);
    return response.data.fornitore;
  }

  async aggiornaFornitore(id: number, dati: Partial<CeFornitore>): Promise<CeFornitore> {
    const response = await this.api.put(`/fornitori/${id}`, dati);
    return response.data.fornitore;
  }

  async disattivaFornitore(id: number): Promise<void> {
    await this.api.delete(`/fornitori/${id}`);
  }

  /** I nomi scritti a mano negli ingredienti che non sono ancora abbinati. */
  async getNomiDaAbbinare(): Promise<NomeDaAbbinare[]> {
    const response = await this.api.get('/fornitori/da-abbinare');
    return response.data.nomi;
  }

  /** Abbina un nome a un fornitore, e ricorda la decisione. */
  async abbinaFornitore(
    testo: string, fornitoreId: number, registraAlias = true
  ): Promise<{ messaggio: string; ingredienti: { id: number; nome: string }[] }> {
    const response = await this.api.post('/fornitori/abbina', { testo, fornitoreId, registraAlias });
    return response.data;
  }

  async rimuoviAliasFornitore(id: number): Promise<void> {
    await this.api.delete(`/fornitori/alias/${id}`);
  }

  async getConfrontoFornitori(
    da?: string, a?: string
  ): Promise<{ periodo: { da: string; a: string }; righe: ConfrontoFornitore[] }> {
    const response = await this.api.get('/fornitori/confronto', { params: { da, a } });
    return response.data;
  }

  async getSchedaDipendente(utenteId: number): Promise<SchedaDipendente> {
    const response = await this.api.get(`/documenti-personale/scheda/${utenteId}`);
    return response.data;
  }

  async salvaAnagraficaDipendente(
    utenteId: number, dati: Partial<Record<string, string | null>>
  ): Promise<AnagraficaDipendente> {
    const response = await this.api.put(`/documenti-personale/anagrafica/${utenteId}`, dati);
    return response.data;
  }

  /** Calcola il costo orario dalle ultime buste paga; applica=false per la sola anteprima. */
  async costoOrarioDaBuste(
    utenteId: number, quante = 3, applica = true
  ): Promise<CalcoloCostoOrario> {
    const response = await this.api.post(
      `/documenti-personale/costo-orario/${utenteId}`, { quante, applica }
    );
    return response.data;
  }

  async getRiepilogoDocumenti(): Promise<RiepilogoDocumenti[]> {
    const response = await this.api.get('/documenti-personale/riepilogo');
    return response.data;
  }

  async getDocumentiUtente(utenteId: number): Promise<DocumentoPersonale[]> {
    const response = await this.api.get(`/documenti-personale/utente/${utenteId}`);
    return response.data;
  }

  async getMieiDocumenti(): Promise<DocumentoPersonale[]> {
    const response = await this.api.get('/documenti-personale/miei');
    return response.data;
  }

  async scaricaDocumento(documentoId: number): Promise<Blob> {
    const response = await this.api.get(`/documenti-personale/${documentoId}/download`, {
      responseType: 'blob'
    });
    return response.data;
  }

  async importaDocumentiMassivo(files: File[]): Promise<EsitoImportMassivo> {
    const formData = new FormData();
    files.forEach((f) => formData.append('file', f));
    const response = await this.api.post('/documenti-personale/import-massivo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async importaTimbrature(files: File[]): Promise<EsitoTimbrature> {
    const formData = new FormData();
    files.forEach((f) => formData.append('file', f));
    const response = await this.api.post('/turni/timbrature/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async getOreConsuntive(dataDa: string, dataA: string): Promise<any[]> {
    const response = await this.api.get('/turni/timbrature/ore', { params: { dataDa, dataA } });
    return response.data;
  }

  async getCeStoricoImport(): Promise<any[]> {
    const response = await this.api.get('/controllo-gestione/import/storico');
    return response.data;
  }

  async getCeMovimenti(filtri: {
    stato?: string; contoId?: number; categoriaId?: number;
    dataDa?: string; dataA?: string; cerca?: string;
    limite?: number; offset?: number;
  } = {}): Promise<{ movimenti: CeMovimento[]; totale: number }> {
    const response = await this.api.get('/controllo-gestione/movimenti', { params: filtri });
    return response.data;
  }

  async getCeSuggerimenti(movimentoId: number): Promise<CeSuggerimento[]> {
    const response = await this.api.get(`/controllo-gestione/movimenti/${movimentoId}/suggerimenti`);
    return response.data;
  }

  async classificaCeMovimento(movimentoId: number, dati: {
    categoriaId?: number; fornitoreId?: number | null; stato?: string;
    note?: string; creaRegola?: boolean; patternRegola?: string;
  }): Promise<any> {
    const response = await this.api.put(`/controllo-gestione/movimenti/${movimentoId}/classifica`, dati);
    return response.data;
  }

  async riclassificaCeMovimenti(): Promise<{ esaminati: number; classificati: number }> {
    const response = await this.api.post('/controllo-gestione/riclassifica');
    return response.data;
  }

  async getCeCategorie(): Promise<CeCategoria[]> {
    const response = await this.api.get('/controllo-gestione/categorie');
    return response.data;
  }

  async getCeFornitori(): Promise<CeFornitore[]> {
    const response = await this.api.get('/controllo-gestione/fornitori');
    return response.data;
  }

  async getCeRegole(): Promise<CeRegola[]> {
    const response = await this.api.get('/controllo-gestione/regole');
    return response.data;
  }

  async aggiornaCeRegola(id: number, dati: Partial<CeRegola>): Promise<CeRegola> {
    const response = await this.api.put(`/controllo-gestione/regole/${id}`, dati);
    return response.data.regola;
  }

  async eliminaCeRegola(id: number): Promise<void> {
    await this.api.delete(`/controllo-gestione/regole/${id}`);
  }

  /** Le regole imparate dalle fatture: codici abbinati e voci scartate. */
  async getRegoleArticoli(): Promise<{
    abbinati: RegolaArticolo[];
    ignorati: RegolaArticoloIgnorato[];
  }> {
    const response = await this.api.get('/controllo-gestione/regole/articoli');
    return response.data;
  }

  /** Toglie l'abbinamento: le righe gia' abbinate con quel codice tornano in attesa. */
  async eliminaRegolaArticolo(id: number): Promise<{ righeRimesseInAttesa: number }> {
    const response = await this.api.delete(`/controllo-gestione/regole/articoli/${id}`);
    return response.data;
  }

  async eliminaRegolaArticoloIgnorato(id: number): Promise<{ righeRimesseInAttesa: number }> {
    const response = await this.api.delete(`/controllo-gestione/regole/articoli-ignorati/${id}`);
    return response.data;
  }

  async getContoEconomico(dataDa: string, dataA: string, base: 'cassa' | 'competenza' = 'cassa'): Promise<ContoEconomico> {
    const response = await this.api.get('/controllo-gestione/conto-economico', {
      params: { dataDa, dataA, base }
    });
    return response.data;
  }

  async getCeRiconciliazione(dataDa: string, dataA: string): Promise<CeRiconciliazione> {
    const response = await this.api.get('/controllo-gestione/riconciliazione', {
      params: { dataDa, dataA }
    });
    return response.data;
  }
}

const apiService = new ApiService();

export default apiService;
